import React, { useState, useEffect } from "react";
import { Table, Input, Select, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { MdAddBox, MdDelete, MdEdit } from "react-icons/md";
import { useGetProductMutation, useDeleteProductMutation, useGetCategoriesMutation } from "../../redux/slices/productSlice";
import debounce from '../../HelperFunctions';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

const Homepage = () => {
    const navigate = useNavigate();
    const [getProduct, { isLoading }] = useGetProductMutation();
    const [getCategories, { isLoading: isCategoriesLoading }] = useGetCategoriesMutation();
    const [deleteProduct] = useDeleteProductMutation();
    const [filteredData, setFilteredData] = useState([]);
    const [productPerPage, setProductPerPage] = useState(10);
    const [productPage, setProductPage] = useState(1)
    const [productTotal, setProductTotal] = useState(0)
    const [categories, setCategories] = useState([])
    const [search, setSearch] = useState(null)
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const getProductData = async (query) => {
        try {
            const res = await getProduct(query);
            setProductTotal(res?.data?.total)
            setFilteredData(res?.data?.data);
        } catch (error) {
            console.error("error", error);
        }
    }
    useEffect(() => {
        let query = `?per_page=${productPerPage}&page=${productPage}`
        if (selectedCategories?.length > 0) {
            query += `&category_id=${JSON.stringify(selectedCategories)}`
        }

        if (search) {
            query += `&search=${search}`
        }

        if (sortField && sortOrder) {
            query += `&sort_by=${sortField}&sort_direction=${sortOrder}`;
        }
        getProductData(query);
    }, [productPerPage, productPage, selectedCategories, sortField, search, sortOrder]);


    const generatePageSizeOptions = (total) => {
        const options = [];
        const maxPageSize = Math.min(total, 100);

        for (let i = 5; i <= maxPageSize; i += 5) {
            options.push(i);
        }
        if (total > 5 && !options.includes(10)) {
            options.push(10);
        }

        if (total > 10 && !options.includes(15)) {
            options.push(15);
        }
        return options.sort((a, b) => a - b);
    };

    const pageSizeOptions = generatePageSizeOptions(productTotal);

    const onPageSizeChange = (current, size) => {
        setProductPerPage(size);
        setProductPage(1);
    };


    const convertBoxesData = (data) => {
        try {
            const firstParsed = JSON.parse(data);
            const finalValue = JSON.parse(firstParsed);
            const totalBoxQuantity = finalValue.reduce(
                (total, item) => total + parseInt(item.boxquantity, 10),
                0
            );
            return totalBoxQuantity;
        } catch (error) {
            return "N/A";
        }
    };



    const tableColumns = [
        {
            title: "S.No",
            key: "serial_number",
            render: (text, record, index) =>
                <span>{(productPage - 1) * productPerPage + index + 1}</span>,
        },
        {
            title: "Product",
            dataIndex: "item_name",
            key: "item_name",
            sorter: true,
            render: (text, record) => (
                <div className="productBox" style={{ display: "flex", alignItems: "center" }}>
                    <div className="imgBox" style={{ marginRight: "20px" }}>
                        {record?.image ? (
                            <img
                                src={record?.image}
                                alt={record?.item_name}
                                className="img-fluid"
                                style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "5px" }}
                            />
                        ) : (
                            <p className="prouct-image-text">Product Image</p>
                        )}

                    </div>
                    <span style={{ textTransform: 'capitalize', fontWeight: '600' }}>{record.item_name}</span>
                </div>
            ),
        },
        {
            title: "Company name",
            dataIndex: "company_name",
            key: "company",
            sorter: true,
            // sorter: (a, b) => a.company.length - b.company.length,
            render: (text, record) => (
                <>
                    <span>{record?.company_name ? record?.company_name : "N/A"}</span>
                </>
            ),
        },
        {
            title: "Vendor name",
            dataIndex: "vendor",
            key: "vendor",
            sorter: true,
            // sorter: (a, b) => a.vendor.length - b.vendor.length,
            render: (text, record) => (
                <>
                    <span>{record?.vendor_name ? record?.vendor_name : "N/A"}</span>
                </>
            ),
        },
        {
            title: "Created By",
            dataIndex: "created_by",
            key: "created_by",
            sorter: true,
            // sorter: (a, b) => a.vendor.length - b.vendor.length,
            render: (text, record) => (
                <span>{record?.created_by ?? "N/A"}</span>
            ),
        },
        {
            title: "Category",
            dataIndex: "category_name",
            key: "category_name",
            sorter: true,
            // sorter: (a, b) => a.category.length - b.category.length,
            render: (text, record) => (
                <span>{record?.category?.name ?? "N/A"}</span>
            ),
        },
        {
            title: "Price",
            dataIndex: "price",
            key: "price",
            sorter: true,
            // sorter: (a, b) => a.price.length - b.price.length,
            render: (text, record) => (
                <span>{record?.price ?? "N/A"}</span>
            ),
        },
        {
            title: "In Stock",
            dataIndex: "quantity",
            key: "quantity",
            sorter: true,
            render: (text, record) => {
                const boxData = record?.boxes;
                const quantity = record?.quantity;

                if (boxData) {
                    try {
                        return <span>{convertBoxesData(boxData)}</span>;
                    } catch {
                        return <span>N/A</span>;
                    }
                }
                return <span>{quantity || "N/A"}</span>;
            },
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            sorter: true,
            render: (text, record) => (
                <span>{record?.created_at ? dayjs(record.created_at).format('MM/DD/YYYY') : "N/A"}</span>
            ),
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (text, record) => (
                <div className="actionButton">
                    <button type="button" className="btn primaryBtn" onClick={(e) => {
                        e.stopPropagation();
                        navigate("/edit-product", {
                            state: { productId: record.id },
                        });
                    }}
                    >
                        <MdEdit style={{ cursor: "pointer", color: '#b4a099' }} />
                    </button>
                    <button type="button" className="btn primaryBtn" onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(record.id);
                    }}
                    >
                        <MdDelete
                            style={{ cursor: "pointer", color: '#ff0000' }}
                        />
                    </button>
                </div>

            ),
        },
    ];

    const handleDelete = async (id) => {
        try {
            Swal.fire({
                title: "Are you sure you want to delete this product?",
                showDenyButton: true,
                icon: "warning",
                confirmButtonText: "Yes",
                denyButtonText: "No",
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const response = await deleteProduct(id); // Await the deleteProduct function
                    const { product, message } = response.data;

                    if (product) {
                        Swal.fire({
                            title: message,
                            icon: "success",
                        });
                        let query = `?per_page=${10}&page=${1}`
                        if (selectedCategories?.length > 0) {
                            query += `&category_id=${JSON.stringify(selectedCategories)}`
                        }

                        if (search) {
                            query += `&search=${search}`
                        }
                        getProductData(query);
                    } else if (response.error) {
                        Swal.fire({
                            title: response.error.data.message,
                            icon: "error",
                            confirmButtonText: 'OK',
                        });
                        return;
                    }
                } else if (result.isDenied) {
                    Swal.fire({
                        title: "Your product is safe!",
                        icon: "info",
                        confirmButtonText: "OK",
                    });
                }
            });


        } catch (error) {
            console.error("Error deleting product:", error);
            Swal.fire({
                title: "Error deleting product",
                icon: "error",
                confirmButtonText: 'OK',
            });
        }
    };

    const getCategoriesList = () => {
        getCategories().then((response) => {
            setCategories(response?.data?.categories)
        }).catch((error) => {
            console.error({ error })
        })
    }

    const handleAddProduct = () => {
        navigate("/add-product");
    };

    const handleRowClick = (record) => {
        navigate("/product-details", {
            state: { productId: record.id },
        });
    };

    const handleCategoryChange = (value) => {
        setSelectedCategories(value)
    }

    const handleSearchProduct = async (event) => {
        setProductPage(1)
        setSearch(event?.target?.value);
    };

    // Handle Select All
    const handleSelectAll = () => {
        const allIds = categories.map((option) => option.id);
        setSelectedCategories(allIds);
    };

    // Handle Unselect All
    const handleUnselectAll = () => {
        setSelectedCategories([]);
    };

    const handleTableChange = (pagination, filters, sorter) => {
        if (sorter.order) {
            setSortField(sorter.field);
            setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
        } else {
            setSortField(null);
            setSortOrder(null);
        }
    };

    return (
        <div className="wrapper productCatalogWrapper">
            <div className="container-fluid">
                <div className="row mb-5">
                    <div className="col-md-12">
                        <div className="titleBar titleRow">
                            <h4>Products Catalogs</h4>
                            <div className="rightWrapper">
                            <div className='inputContainer me-3 product-search' style={{marginBottom: '0px'}}>
                            <label htmlFor="lastVisitSelect" className="dynamicLabel">Search Here</label>
                                <Input
                                    placeholder="Search here"
                                    onChange={handleSearchProduct}
                                    className="homesearchInput titleRowInput customProductSearch pe-2"
                                    style={{border:"none",height:"49px", width:"100%", paddingRight:"10px"}}
                                    allowClear
                                />
                                 </div>
                                {/* <Select
                                    placeholder="Select Parent Category"
                                    className="formSelect category-select me-3"
                                    onClick={() => categories?.length === 0 ? getCategoriesList() : <></>}
                                    onChange={(e) => {
                                        handleCategoryChange(e)
                                    }}
                                    notFoundContent={isCategoriesLoading ? (
                                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                            <Spin />
                                        </div>
                                    ) : null}
                                    options={categories?.map((option) => ({
                                        value: option.id,
                                        label: option.name,
                                    }))}
                                /> */}
                                  <div className='inputContainer' style={{marginBottom: '0px'}}>
                                  <label htmlFor="lastVisitSelect" className="dynamicLabel">Select Category</label>
                                <Select
                                style={{height:"49px"}}
                                    mode="multiple"
                                    value={selectedCategories}
                                    placeholder="Select Category"
                                    className="formSelect category-select me-3"
                                    onClick={() => {
                                        if (categories?.length === 0) getCategoriesList();
                                    }}
                                    onChange={(e) => handleCategoryChange(e)}
                                    notFoundContent={
                                        isCategoriesLoading ? (
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                <Spin />
                                            </div>
                                        ) : "No categories found"
                                    }
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    dropdownRender={(menu) => (
                                        <>
                                            <div style={{ padding: "8px" }}>
                                                {selectedCategories?.length === categories?.length ? (
                                                    <span onClick={handleUnselectAll} style={{ cursor: "pointer" }}>
                                                        Unselect All
                                                    </span>
                                                ) : (
                                                    <span onClick={handleSelectAll} style={{ cursor: "pointer" }}>
                                                        Select All
                                                    </span>
                                                )}
                                            </div>
                                            {menu}
                                        </>
                                    )}
                                >
                                    {categories?.map((option) => (
                                        <Select.Option key={option.id} value={option.id}>
                                            {option.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                                </div>

                                <div className="buttonWrap">
                                    <button
                                    style={{height:"49px"}}
                                        type="button"
                                        className="btn backBtn"
                                        onClick={() => handleAddProduct()}
                                    >
                                        <MdAddBox /> Add Product
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="tableWrapper">
                            <Table
                                className="productTable"
                                columns={tableColumns}
                                dataSource={isLoading ? [] : filteredData}
                                pagination={{
                                    current: productPage,
                                    pageSize: productPerPage,
                                    total: productTotal,
                                    onChange: (page) => setProductPage(page),
                                    onShowSizeChange: onPageSizeChange,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    pageSizeOptions: pageSizeOptions,
                                }}
                                loading={isLoading}
                                onChange={handleTableChange}
                                onRow={(record) => ({
                                    onClick: () => handleRowClick(record),
                                })}
                                showSorterTooltip={false}
                                scroll={{ x: 'auto' }}
                                bordered
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Homepage;
